// pipe 4C: the SHARED ERP table controls — a per-page filter Input and a sortable column header —
// reused by the Operations worklist, Records, and Customers screens (SSOT/DRY, the same pattern as
// transition-affordance.tsx / erp-page-header.tsx). Both are PURE presenters.
//
// Import discipline (load-bearing, mirrors erp-table.tsx): this file imports ONLY the
// @affine/component ui/* subpaths (Input, TableCell — both resolve under happy-dom; the portal
// must not pull the barrel), @blocksuite/icons/rc, @affine/i18n (mocked under happy-dom), the pure
// sort types, the host-provided info-tip slot, and its css module. NO runtime ErpService /
// workbench / DI / Electron — and NO @affine/core.
import { Input } from '@affine/component/ui/input';
import { TableCell } from '@affine/component/ui/table';
import { useI18n } from '@affine/i18n';
import {
  CloseIcon,
  SearchIcon,
  SortDownIcon,
  SortUpIcon,
} from '@blocksuite/icons/rc';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ErpInfoTipSlot } from './erp-info-tip-slot';
import * as styles from './erp-table-controls.css';
import type { SortState } from './erp-table-filter';

export interface TableFilterInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional screen-specific placeholder; defaults to the shared "Filter rows…" label. */
  placeholder?: string;
  /** Override for the how-this-table-works tip — a screen whose grid lacks sort/resize (the
   *  training matrix) passes a reduced explanation so the tip never over-claims. */
  info?: ReactNode;
}

/** A debounce-free controlled filter box (filtering already-loaded rows is instant — the caller
 *  debounces only if a list is large enough to make re-render cost matter). Search-icon prefix + a
 *  clear button when non-empty. `type="search"` so the platform offers a native clear affordance too. */
export function TableFilterInput({
  value,
  onChange,
  placeholder,
  info,
}: TableFilterInputProps) {
  const t = useI18n();
  const label = placeholder ?? t['com.affine.pipe.table.filter-placeholder']();
  return (
    <>
      <Input
        className={styles.filterInput}
        type="search"
        value={value}
        onChange={onChange}
        placeholder={label}
        aria-label={label}
        preFix={<SearchIcon className={styles.filterIcon} />}
        endFix={
          value ? (
            <button
              type="button"
              className={styles.clearButton}
              aria-label={t['com.affine.pipe.table.filter-clear']()}
              onClick={() => onChange('')}
            >
              <CloseIcon />
            </button>
          ) : null
        }
      />
      {/* The one place every grid explains itself: filter, sort, resize, copy gestures. Rendered
          through the host-provided slot (nothing without a provider — see erp-info-tip-slot). */}
      <ErpInfoTipSlot
        content={info ?? t['com.affine.pipe.tooltip.table']()}
        side="bottom"
        data-testid="erp-table-info-tip"
      />
    </>
  );
}

// --- Column resize (grid UX) --------------------------------------------------------------
// The shared @affine/component Table lays out with `table-layout: fixed`, so an explicit width
// on a column's HEADER cell resizes the whole column — body cells follow, and whatever stays
// clipped keeps the ErpTable hover-reveal. (Records' auto-layout grid gets best-effort resize:
// auto layout treats the width as a strong hint.) Widths persist per COLUMN KEY, deliberately
// shared across screens — `data.status` is the same column everywhere, so it stays consistent.
const COL_WIDTH_STORAGE_PREFIX = 'pipe.erp.col-width.';
const MIN_COL_WIDTH = 64;
// The absolute ceiling (also the aria-valuemax): the per-grid clamp below is tighter on any
// real viewport — this bound just keeps a garbage stored value from ever rendering.
const MAX_COL_WIDTH = 4096;
const KEYBOARD_RESIZE_STEP = 16;

function readStoredWidth(columnKey: string): number | undefined {
  try {
    const raw = localStorage.getItem(COL_WIDTH_STORAGE_PREFIX + columnKey);
    const width = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(width) &&
      width >= MIN_COL_WIDTH &&
      width <= MAX_COL_WIDTH
      ? width
      : undefined;
  } catch {
    return undefined;
  }
}

function persistWidth(columnKey: string, width: number | undefined) {
  try {
    if (width === undefined) {
      localStorage.removeItem(COL_WIDTH_STORAGE_PREFIX + columnKey);
    } else {
      localStorage.setItem(
        COL_WIDTH_STORAGE_PREFIX + columnKey,
        String(Math.round(width))
      );
    }
  } catch {
    // Storage unavailable (private mode): the resize still applies for this session.
  }
}

/** Clamp a resize to the grid's own viewport so one column can never push every other one out
 *  of reach (the gridWrapper scrolls horizontally well before this bound bites). */
function clampWidth(width: number, cell: HTMLElement): number {
  const wrapper = cell.closest('div');
  const max = Math.min(
    MAX_COL_WIDTH,
    Math.max(320, (wrapper?.clientWidth ?? 960) - 96)
  );
  return Math.min(max, Math.max(MIN_COL_WIDTH, width));
}

export interface SortableHeaderCellProps {
  /** The already-localized column label. */
  label: string;
  /** Must match the `SortColumn.key` / `SortState.key` for this column. */
  columnKey: string;
  sort: SortState;
  onToggle: (key: string) => void;
  /** Non-sortable columns (e.g. an Actions column) render as plain header text. Default true. */
  sortable?: boolean;
  /** The shared `headerCell` style from the screen's css (so the header keeps its screen styling). */
  className?: string;
}

/** A column header that toggles this table's sort on click (asc → desc → cleared), mirroring AFFiNE's
 *  own list-header convention (SortUp/SortDown icons) but with a real <button> + `role="columnheader"`
 *  + `aria-sort` and a state-describing accessible name, so screen readers announce the sort. */
export function SortableHeaderCell({
  label,
  columnKey,
  sort,
  onToggle,
  sortable = true,
  className,
}: SortableHeaderCellProps) {
  const t = useI18n();
  const [width, setWidth] = useState<number | undefined>(() =>
    readStoredWidth(columnKey)
  );
  const cellRef = useRef<HTMLTableCellElement | null>(null);

  // A width stored on a wide desktop must not lock a phone viewport out of its own grid: the
  // RESTORED width is clamped to this device once, display-only (storage keeps the wide value,
  // so the desktop layout survives the phone visit).
  useLayoutEffect(() => {
    const cell = cellRef.current;
    if (!cell) return;
    setWidth(w => (w === undefined ? w : clampWidth(w, cell)));
  }, []);

  const applyWidth = (next: number | undefined) => {
    setWidth(next);
    persistWidth(columnKey, next);
  };

  const onResizePointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const handle = e.currentTarget;
    const cell = handle.closest('td');
    if (!cell) return;
    // Keep the gesture off the sort button and off text selection.
    e.preventDefault();
    e.stopPropagation();
    const rect = cell.getBoundingClientRect();
    // RTL (ar): the drag grows the column toward the inline-start, so measure from the right.
    const rtl = getComputedStyle(cell).direction === 'rtl';
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      // happy-dom / older engines: uncaptured moves still reach the handle in tests.
    }
    let latest = width ?? rect.width;
    let moved = false;
    const onMove = (ev: PointerEvent) => {
      moved = true;
      latest = clampWidth(
        rtl ? rect.right - ev.clientX : ev.clientX - rect.left,
        cell
      );
      setWidth(latest);
    };
    const end = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      // A zero-move click/tap must stay a no-op — persisting the incidental auto width would
      // silently pin every screen sharing this column key.
      if (moved) persistWidth(columnKey, latest);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  const onResizeKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    const cell = e.currentTarget.closest('td');
    if (!cell) return;
    if (e.key === 'Home' || e.key === 'Delete') {
      e.preventDefault();
      applyWidth(undefined);
      return;
    }
    // Horizontal arrows follow the writing direction (in RTL the splitter sits at the LEFT edge
    // of its column, so ArrowLeft widens); the vertical pair stays direction-neutral.
    const rtl = getComputedStyle(cell).direction === 'rtl';
    const growKey = rtl ? 'ArrowLeft' : 'ArrowRight';
    const shrinkKey = rtl ? 'ArrowRight' : 'ArrowLeft';
    const grow = e.key === growKey || e.key === 'ArrowUp';
    const shrink = e.key === shrinkKey || e.key === 'ArrowDown';
    if (!grow && !shrink) return;
    e.preventDefault();
    const current = width ?? cell.getBoundingClientRect().width;
    applyWidth(
      clampWidth(
        current + (grow ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP),
        cell
      )
    );
  };

  // A focusable window-splitter (role=separator + aria-valuenow): drag or arrow keys resize;
  // Home/Delete or double-click resets to automatic width.
  const resizeHandle = (
    <span
      className={styles.resizeHandle}
      role="separator"
      aria-orientation="vertical"
      tabIndex={0}
      aria-label={t['com.affine.pipe.table.resize-column']({ column: label })}
      aria-valuenow={width === undefined ? undefined : Math.round(width)}
      aria-valuemin={MIN_COL_WIDTH}
      aria-valuemax={MAX_COL_WIDTH}
      onPointerDown={onResizePointerDown}
      onKeyDown={onResizeKeyDown}
      onClick={e => e.stopPropagation()}
      onDoubleClick={e => {
        e.stopPropagation();
        applyWidth(undefined);
      }}
    />
  );
  // min/max mirror width so neither a screen's own header maxWidth (records) nor the fixed
  // layout's redistribution can fight an explicit user resize.
  const widthStyle =
    width === undefined
      ? undefined
      : { width, minWidth: width, maxWidth: width };

  if (!sortable) {
    return (
      <TableCell ref={cellRef} className={className} style={widthStyle}>
        {label}
        {resizeHandle}
      </TableCell>
    );
  }
  const active = sort.key === columnKey;
  const ariaSort = active
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : undefined;
  const ariaLabel = active
    ? t['com.affine.pipe.table.sorted-by']({
        column: label,
        direction: t[`com.affine.pipe.table.sort-${sort.dir}`](),
      })
    : t['com.affine.pipe.table.sort-by']({ column: label });
  return (
    <TableCell
      ref={cellRef}
      className={className}
      style={widthStyle}
      role="columnheader"
      aria-sort={ariaSort}
    >
      <button
        type="button"
        className={styles.sortButton}
        aria-label={ariaLabel}
        onClick={() => onToggle(columnKey)}
      >
        <span className={styles.sortLabel}>{label}</span>
        {active ? (
          <span className={styles.sortIndicator} aria-hidden="true">
            {sort.dir === 'asc' ? <SortUpIcon /> : <SortDownIcon />}
          </span>
        ) : (
          <span className={styles.sortHint} aria-hidden="true">
            <SortDownIcon />
          </span>
        )}
      </button>
      {resizeHandle}
    </TableCell>
  );
}
